import { useCallback, useEffect, useRef, useState } from 'react'

type UseCabinMediaDrawerArgs = {
    isOpen: boolean
    itemCount: number
    initialIndex?: number
    onClose: () => void
}

const useCabinMediaDrawer = ({ isOpen, itemCount, initialIndex = 0, onClose }: UseCabinMediaDrawerArgs) => {
    const [activeIndex, setActiveIndex] = useState(initialIndex)
    const segmentFillRefs = useRef<(HTMLDivElement | null)[]>([])
    const activeIndexRef = useRef(activeIndex)

    useEffect(() => {
        activeIndexRef.current = activeIndex
    }, [activeIndex])

    useEffect(() => {
        if (!isOpen) {
            setActiveIndex(initialIndex)
            segmentFillRefs.current.forEach(fill => {
                if (fill) fill.style.transform = 'scaleX(0)'
            })
            return
        }

        const safeIndex = Math.min(Math.max(initialIndex, 0), Math.max(itemCount - 1, 0))
        setActiveIndex(safeIndex)
    }, [initialIndex, isOpen, itemCount])

    useEffect(() => {
        segmentFillRefs.current.forEach((fill, index) => {
            if (!fill) return
            fill.style.transform = index < activeIndex ? 'scaleX(1)' : 'scaleX(0)'
        })
    }, [activeIndex])

    const handlePlaybackProgress = useCallback((progress: number) => {
        const fill = segmentFillRefs.current[activeIndexRef.current]
        if (fill) {
            fill.style.transform = `scaleX(${progress})`
        }
    }, [])

    const setSegmentFillRef = useCallback(
        (index: number) => (element: HTMLDivElement | null) => {
            segmentFillRefs.current[index] = element
            if (!element) return

            if (index < activeIndexRef.current) {
                element.style.transform = 'scaleX(1)'
            } else if (index > activeIndexRef.current) {
                element.style.transform = 'scaleX(0)'
            }
        },
        []
    )

    const goToNext = useCallback(() => {
        if (itemCount <= 1) return
        setActiveIndex(prev => (prev < itemCount - 1 ? prev + 1 : 0))
    }, [itemCount])

    const goToPrevious = useCallback(() => {
        if (activeIndex > 0) {
            setActiveIndex(prev => prev - 1)
        }
    }, [activeIndex])

    const handleMediaComplete = useCallback(() => {
        goToNext()
    }, [goToNext])

    useEffect(() => {
        if (!isOpen) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
                return
            }

            if (itemCount <= 1) return

            if (event.key === 'ArrowLeft') {
                goToPrevious()
            }

            if (event.key === 'ArrowRight') {
                goToNext()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [goToNext, goToPrevious, isOpen, itemCount, onClose])

    const canGoPrevious = activeIndex > 0
    const showNavigation = itemCount > 1

    return {
        activeIndex,
        canGoPrevious,
        showNavigation,
        goToPrevious,
        goToNext,
        handlePlaybackProgress,
        handleMediaComplete,
        setSegmentFillRef,
    }
}

export default useCabinMediaDrawer
